import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import {
  ApiButton,
  ContentTemplate,
  Divider,
  HeaderTemplate,
  KeyValue,
  SectionHeader,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import RouteKey from '../../../routes/key';
import { useGetExamTypes } from '../../../api-refactored/candidate/useGetExamTypes';
import {
  GetEligibilityCheckingResponse,
  useGetEligibilityChecking,
} from '../../../api-refactored/group-exam-registration/useGetEligibilityChecking';
import ExamInstruction from './ExamInstruction';
import ExamNotAvailable from '../../../modules/examRegistration/components/ExamNotAvailable';
import { usePostCreateGroupExamRegistration } from '../../../api-refactored/group-exam-registration/usePostCreateGroupExamRegistration';
import Acknowledgement from '../../../components/Acknowledgement';
import GroupExamRegistrationTitleHeader from '../../../components/GroupExamRegistrationTitleHeader';
import { ExamRegistrationType } from '../../../api/candidate/exam/model';
import { parseLocationSearch } from '../../../utils/StringUtils';
import ExamDraftApplication from '../../../modules/examRegistration/components/ExamDraftApplication';
import { usePostTransactionLog } from '../../../api-refactored/candidate/usePostTransactionLog';
import { useWOGAA } from '../../../hooks/wogaa';
import ProceedGroupSupplementaryModal from '../../../modules/groupExamRegistration/components/ProceedGroupSupplementaryModal';
import { GenericErrorResponse } from '../../../api-refactored/typings';
import {typeTheme} from "web-library/theme/helper/tssHelper"

const { exam } = RouteKey.child;
const { coordinatorExamRegistrations, bulkUploadCandidates } = RouteKey.exam.child;
const useStyles = makeStyles()((theme) =>
({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  privateCentreLabel: {
     ...typeTheme(theme, 'body1bold'),
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(1),
  },
  privateCentreValue: {
     ...typeTheme(theme, 'inputText'),
    marginBottom: theme.spacing(5),
  },
  sectionHeader: {
    marginBottom: theme.spacing(4),
  },
}));

const checkAcknowledgement = (
  callback: () => void,
  flags: {
    isDeclared: boolean;
    waiveLateFee: boolean;

    requiredSupplementaryTransition: boolean;
  },
  checkEligibilityData: any,
  setAcknowledgementAlert: (setAlert: boolean) => void,
  setProceedSupplementaryModal: (setOpen: boolean) => void
) => {
  if (
    checkEligibilityData?.examRegistrationType === ExamRegistrationType.SUPPLEMENTARY &&
    !flags.isDeclared &&
    !flags.waiveLateFee
  ) {
    setAcknowledgementAlert(true);
  } else if (flags.requiredSupplementaryTransition) {
    setProceedSupplementaryModal(true);
  } else {
    setAcknowledgementAlert(false);
    callback();
  }
};

const useExamTypeDescription = (examType: any) => {
  const getExamTypes = useGetExamTypes({});
  return getExamTypes.data?.find((data) => data.code === examType)?.description;
};

const confirmButtonLabel = (
  requiredSupplementaryTransition: boolean,
  t: TFunction,
  checkEligibilityData: GetEligibilityCheckingResponse | undefined
) =>
  checkEligibilityData?.draft?.applicationId && !requiredSupplementaryTransition
    ? t('exam->>RegisterExamInstructions.ContinueRegistration')
    : t('exam->>RegisterExamInstructions.StartRegistration');

const onEligibilityCheckingError = (
  err: AxiosError<GenericErrorResponse>,
  setEligibleErrorCode: (eligibleErrorCode: string) => void
) => {
  setEligibleErrorCode(err.response?.data.code || '');
};

const onEligibilityCheckingSuccess = (
  res: GetEligibilityCheckingResponse,
  setExamTitle: () => void,
  setRequiredSupplementaryTransition: (requiredSupplementaryTransition: boolean) => void,
  setEligibleErrorCode: (eligibleErrorCode: string) => void
) => {
  if (res.examRegistrationType === ExamRegistrationType.SUPPLEMENTARY) {
    setExamTitle();
  }

  if (res.draft?.examRegistrationType === ExamRegistrationType.EXAM) {
    setRequiredSupplementaryTransition(res.examRegistrationType !== ExamRegistrationType.EXAM);
  }

  if (res.examRegistrationType) {
    setEligibleErrorCode('');
  }
};

const CoordinatorRegisterExam = () => {
  const history = useHistory();
  const { examType } = useParams<any>();

  const location = useLocation();
  const { token = null } = parseLocationSearch(location.search);
  const { state: locationState } = useLocation<{ waiveLateFee: boolean }>();
  const waiveLateFee: boolean = locationState && locationState.waiveLateFee;

  const { t } = useTranslation();
  const { classes } = useStyles();
  const [eligibleErrorCode, setEligibleErrorCode] = useState('');
  const [examTitle, setExamTitle] = useState<string>(t('exam->>RegisterExam.RegisterForAnExam'));
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [acknowledgementAlert, setAcknowledgementAlert] = useState<boolean>(false);
  const [requiredSupplementaryTransition, setRequiredSupplementaryTransition] = useState<boolean>(
    false
  );
  const [proceedSupplementaryModal, setProceedSupplementaryModal] = useState<boolean>(false);

  const postCreateExamRegistration = usePostCreateGroupExamRegistration({
    examType,
    token,
    onSuccess: (res) =>
      history.push(
        `/${exam}/${coordinatorExamRegistrations.route}/${res.uuid}/${bulkUploadCandidates.route}`,
        { token }
      ),
  });

  const { mutate: apiMutation, status: postStatus } = usePostTransactionLog({
    onSuccess: () => {
      history.push(
        `/${exam}/${coordinatorExamRegistrations.route}/${checkEligibilityData?.draft?.applicationUuid}/${bulkUploadCandidates.route}`,
        { token }
      );
    },
  });

  const { data: checkEligibilityData, refetch: checkEligibility } = useGetEligibilityChecking({
    examType,
    token,
    onError: (err) => {
      onEligibilityCheckingError(err, setEligibleErrorCode);
    },
    onSuccess: (res) =>
      onEligibilityCheckingSuccess(
        res,
        () => setExamTitle(t('exam->>SupplementaryRegistration.RegisterForAnExamSupplementary')),
        setRequiredSupplementaryTransition,
        setEligibleErrorCode
      ),
  });
// wogaa tracking for coordinator exam registration //
  const { transactionStart } = useWOGAA('myexamsseab-6454');

  const handleStartRegistration = () => {
    transactionStart();
    postCreateExamRegistration.mutate();
  };

  const getExamTypeDescription = useExamTypeDescription(examType);

  // To re-check the eligibility after changing the target exam type.
  useEffect(() => {
    checkEligibility();
  }, [examType, checkEligibility]);

  const handleAgreement = () => {
    setAcknowledgementAlert(false);
    setIsDeclared(!isDeclared);
  };

  const handleContinueRegistration = () => {
    transactionStart();
    apiMutation({
      auditActionType: 'CONTINUE_EXAM_REGISTRATION',
      applicationId: `${checkEligibilityData?.draft?.applicationId}`,
    });
  };

  if (eligibleErrorCode && getExamTypeDescription) {
    return (
      <ExamNotAvailable
        examTitle={examTitle}
        examTypeDisplay={getExamTypeDescription}
        eligibleErrorCode={eligibleErrorCode}
      />
    );
  }

  const buttonLabel = confirmButtonLabel(requiredSupplementaryTransition, t, checkEligibilityData);

  return (
    <>
      <GroupExamRegistrationTitleHeader
        examRegistrationType={checkEligibilityData?.examRegistrationType}
        t={t}
      />

      <HeaderTemplate>
        <KeyValue
          keyText={t('exam->>RegisterExamInstructions.Exam Level')}
          valueText={getExamTypeDescription}
        />
      </HeaderTemplate>
      {checkEligibilityData && (
        <ContentTemplate type="large">
          <ExamInstruction
            examType={examType}
            examRegistrationType={checkEligibilityData?.examRegistrationType}
          />
          <Divider paddingTop />
          <SectionHeader
            className={classes.sectionHeader}
            title={t('exam->>RegisterExamInstructions.GetStarted')}
          />
          {checkEligibilityData.draft?.applicationId && !requiredSupplementaryTransition ? (
            <>
              <ExamDraftApplication
                examLevels={checkEligibilityData.draft.examLevels}
                examYear={checkEligibilityData.draft.examYear}
                applicationId={checkEligibilityData.draft.applicationId}
                examType={checkEligibilityData.draft.examType}
                displayExamType={examType}
              />
            </>
          ) : (
            <>
              <Typography variant="h5" className={classes.privateCentreLabel}>
                {t('exam->>RegisterExamInstructions.PrivateCentre')}
              </Typography>
              <Typography variant="h5" className={classes.privateCentreValue}>
                {checkEligibilityData.organisationName}
              </Typography>
            </>
          )}
          {checkEligibilityData.examRegistrationType === ExamRegistrationType.SUPPLEMENTARY &&
            !waiveLateFee && (
              <Acknowledgement
                isAgreed={isDeclared}
                handleAgreement={handleAgreement}
                showError={acknowledgementAlert}
                content={t('exam->>SupplementaryRegistration.AcknowledgementMessage')}
              />
            )}
          <ApiButton
            id="button-registration"
            status={postCreateExamRegistration.status && postStatus}
            onClick={() =>
              checkAcknowledgement(
                checkEligibilityData.draft?.applicationId
                  ? handleContinueRegistration
                  : handleStartRegistration,
                {
                  isDeclared,
                  waiveLateFee,
                  requiredSupplementaryTransition,
                },
                checkEligibilityData,
                setAcknowledgementAlert,
                setProceedSupplementaryModal
              )
            }
          >
            {buttonLabel}
          </ApiButton>

          <ProceedGroupSupplementaryModal
            modelOpen={proceedSupplementaryModal}
            setModelOpen={setProceedSupplementaryModal}
            callbackMethod={(uuid: string) => {
              history.push(
                `/${exam}/${coordinatorExamRegistrations.route}/${uuid}/${bulkUploadCandidates.route}`,
                { token }
              );
            }}
            draftUuid={checkEligibilityData.draft?.applicationUuid}
            examType={checkEligibilityData.draft?.examType}
          />
        </ContentTemplate>
      )}
    </>
  );
};

export default CoordinatorRegisterExam;
